/**
 * Global Navigation Configuration
 * Centralized role-aware navigation items for all pages
 */

import type { UserRole } from '../types/user'

export interface NavigationItem {
  id: string
  label: string
  icon: string
  href?: string
  badge?: string | number
  roles?: UserRole[] // Roles that can see this item (empty = all roles)
}

/**
 * Admin navigation items
 * Full access to team management, analytics, scheduler, content, and settings
 */
export const adminNavigationItems: NavigationItem[] = [
  { id: 'content', label: 'Team Content', icon: 'file-text', href: '/admin/team-content', roles: ['admin'] },
  { id: 'dashboard', label: 'My team', icon: 'users', href: '/admin/dashboard', roles: ['admin'] },
  { id: 'analytics', label: 'Analytics', icon: 'bar-chart', href: '/admin/analytics', roles: ['admin'] },
  { id: 'scheduler', label: 'Scheduler', icon: 'calendar', href: '/admin/scheduler', roles: ['admin'] },
  { id: 'settings', label: 'Settings', icon: 'settings', href: '/admin/settings', roles: ['admin'] },
  { id: 'activity-log', label: 'Activity Log', icon: 'presentation-chart-line', href: '/admin/activity-log', roles: ['admin'] }
]

/**
 * Editor navigation items
 * Similar to admin interface but without Activity Log and Settings
 */
export const editorNavigationItems: NavigationItem[] = [
  { id: 'content', label: 'Team Content', icon: 'file-text', href: '/editor/content', roles: ['editor'] },
  { id: 'dashboard', label: 'My team', icon: 'users', href: '/editor/dashboard', roles: ['editor'] },
  { id: 'analytics', label: 'Analytics', icon: 'bar-chart', href: '/editor/analytics', roles: ['editor'] },
  { id: 'scheduler', label: 'Scheduler', icon: 'calendar', href: '/editor/scheduler', roles: ['editor'] }
]

/**
 * Profile navigation items (business-connected profiles)
 * For users who belong to an organization - NO settings tab
 */
export const profileNavigationItems: NavigationItem[] = [
  { id: 'dashboard', label: 'Create new', icon: 'plus-circle', href: '/profile/dashboard', roles: ['profile'] },
  { id: 'content', label: 'My Content', icon: 'file-text', href: '/profile/content', roles: ['profile'] },
  { id: 'analytics', label: 'My Analytics', icon: 'bar-chart', href: '/profile/analytics', roles: ['profile'] },
  { id: 'scheduler', label: 'Scheduler', icon: 'calendar', href: '/profile/scheduler', roles: ['profile'] },
  { id: 'library', label: 'My Library', icon: 'book-open', href: '/profile/library', roles: ['profile'] }
  // { id: 'commenter', label: 'Smart Commenter', icon: 'message-circle', href: '/profile/commenter', roles: ['profile'] } // Hidden for now
]

/**
 * Self-serve profile navigation items
 * For users who signed up without an organization invitation
 * Includes Settings tab for user-specific settings
 */
export const selfServeProfileNavigationItems: NavigationItem[] = [
  { id: 'dashboard', label: 'Create new', icon: 'plus-circle', href: '/profile/dashboard', roles: ['profile'] },
  { id: 'content', label: 'My Content', icon: 'file-text', href: '/profile/content', roles: ['profile'] },
  { id: 'analytics', label: 'My Analytics', icon: 'bar-chart', href: '/profile/analytics', roles: ['profile'] },
  { id: 'scheduler', label: 'Scheduler', icon: 'calendar', href: '/profile/scheduler', roles: ['profile'] },
  { id: 'library', label: 'My Library', icon: 'book-open', href: '/profile/library', roles: ['profile'] },
  { id: 'settings', label: 'Settings', icon: 'settings', href: '/profile/settings', roles: ['profile'] }
]

/**
 * Get navigation items based on user role and organization membership
 * @param role - User's current role
 * @param organizationId - User's organization ID (null for self-serve profiles)
 */
export function getNavigationItems(role: UserRole | null, organizationId?: string | null): NavigationItem[] {
  if (role === 'admin') {
    return adminNavigationItems
  }
  if (role === 'editor') {
    return editorNavigationItems
  }
  if (role === 'profile') {
    // Self-serve profiles (no organization) get Settings tab
    if (!organizationId) {
      return selfServeProfileNavigationItems
    }
    // Business-connected profiles don't get Settings tab
    return profileNavigationItems
  }
  // Default to empty if no role (unauthenticated)
  return []
}

/**
 * Legacy navigation items (deprecated - use getNavigationItems instead)
 * @deprecated Use getNavigationItems(role) for role-aware navigation
 */
export const navigationItems: NavigationItem[] = [
  { id: 'team', label: 'Team', icon: 'users' },
  { id: 'analytics', label: 'Analytics', icon: 'bar-chart' },
  { id: 'scheduler', label: 'Scheduler', icon: 'calendar' },
  { id: 'content', label: 'Content', icon: 'file-text' },
  { id: 'settings', label: 'Settings', icon: 'settings' },
  { id: 'activity-log', label: 'Activity Log', icon: 'presentation-chart-line' }
]

